/**
 * Check-in streak derivation (R7.3, ADR-0006 "Backfill -- yesterday only",
 * activated this pass -- `docs/specs/phase-2b-habits.md` §5/AC-B22). Shared
 * by `snapshot.ts` (`RewardSnapshot.streakDays`, day-granular) and
 * `badges.ts` (`checkin_streak_weeks`, week-granular) so both read the exact
 * same definition of "the streak" rather than two independent derivations
 * silently drifting apart.
 */

import type { CompassEvent } from '../../contracts';
import { eventsOfTypes, excludingReverted } from '../event-log/event-log';
import { addDaysToDate } from '../habits/week';

/**
 * Consecutive local calendar days with at least one active `checkin
 * .completed` event, counting back from `today` (R7.3: "an incomplete today
 * never breaks the streak").
 *
 * Algorithm: start the walk at `today` if today already has a check-in,
 * else start at yesterday (today being incomplete is not itself a gap --
 * it simply hasn't happened yet). From the start date, walk backwards one
 * day at a time, counting while each date has a check-in; stop at the first
 * gap. AC-B22's exact case: check-ins on days 1-3, none on day 4 -- queried
 * as of day 4 (before today's tap) reads 3; queried on day 5 (day 4 never
 * got one) reads 0, because day 5's walk starts at day 4 (no check-in that
 * day either) and immediately hits a gap.
 *
 * A day with zero due items (AC-B21's "all caught up" empty state) does NOT
 * count unless it produced an actual tap -- opening the app is not a
 * check-in (phase-2b-habits.md §5: "the streak still requires at least one
 * tap"). This function has no opinion on WHY a day lacks a check-in; it
 * only reads the event log.
 */
export function computeStreakDays(
  events: readonly CompassEvent[],
  today: string,
): number {
  const active = excludingReverted(events);
  // Widened to Set<string>: `today`/`cursor` are plain strings (this module
  // is generic over any ISO date string, not just the branded IsoDate the
  // event envelope carries), so the set must compare on the same base type.
  const checkinDates = new Set<string>(
    eventsOfTypes(active, ['checkin.completed']).map((e) => e.occurredOn as string),
  );

  const yesterday = addDaysToDate(today, -1);
  let cursor = checkinDates.has(today) ? today : yesterday;

  let streak = 0;
  while (checkinDates.has(cursor)) {
    streak += 1;
    cursor = addDaysToDate(cursor, -1);
  }
  return streak;
}
